import React, { useEffect, useState, useMemo } from "react";
import { useAppMutation, useAppQuery } from "../../../api/fetchClient";
import { LanguageTabs, Loader, Card, Button, Modal, PageHeader } from "../../../components/ui";
import { QUERY_KEYS } from "../../../api/queryKeys";
import { ADD_QUE, GET_QUE, UPDATE_QUE, DELETE_QUE, GET_LAUNGUGE } from "../../../api/url";
import QuestionForm from "./QuestionForm.jsx";
import { deleteIcon, editIcon } from "../../../assets/Icons/icons";
import DeletModel from "../../../components/model/deleteModel";
import { toast } from "react-toastify";
import { strings } from "../../../strings/strings.js";

const Question = () => {
  const [activeTab, setActiveTab] = useState("");
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [questionNo, setQuestionNo] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [notes, setNotes] = useState("");
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedNotes, setSelectedNotes] = useState("");
const [originalData, setOriginalData] = useState(null);
  const [language, setLanguage] = useState(null);
  const [formData, setFormData] = useState({ question: "", options: [""] });
  const [formErrors, setFormErrors] = useState({
    questionNo: "",
    question: "",
    options: [],
    general: "",
  });
const MAX_QUESTIONS = 8;

  const { mutate: deleteQuestion, isPending: isDeleting } = useAppMutation();
  const { mutate, isPending: isSaving } = useAppMutation();

  const { data: languagesData } = useAppQuery({
    queryKey: QUERY_KEYS.LANGUAGES,
    url: GET_LAUNGUGE,
    method: "GET",
  });

  const availableLanguages = useMemo(() => {
    const languagesArray = languagesData?.results?.languages || languagesData?.data?.languages || [];

    if (!Array.isArray(languagesArray)) {
      return [];
    }

    const allLanguages = languagesArray.map((lang) => lang?.language_name || lang?.native_name).filter((lang) => lang);

    return allLanguages;
  }, [languagesData]);
  useEffect(() => {
    if (availableLanguages.length > 0 && !language) {
      const firstLang = availableLanguages[0];
      setLanguage(firstLang);
      setActiveTab(firstLang);
    }
  }, [availableLanguages, language]);
  const handleLanguageChange = (langName) => {
    if (!langName) return;
    setLanguage(langName);
    setActiveTab(langName);
    setFormData({ question: "", options: [""] });
    setFormErrors({ questionNo: "", question: "", options: [], general: "" });
    setIsEditing(false);
    setEditingId(null);
    setQuestionNo("");
    setNotes("");
  };

  const isRtl = (langName) => {
    if (!langName) return false;
    const v = String(langName).trim();
    return /^ar$/i.test(v) || /arab/i.test(v) || /عرب/.test(v);
  };

  const isRtlLanguage = isRtl(language);
  const questionNoLabel = isRtlLanguage ? strings.questionarabic : strings.question;

  useEffect(() => {
  
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [language, activeTab]);

  const {
    data: questionsResponse,
    isLoading: isQuestionsLoading,
    refetch: refetchQuestions,
  } = useAppQuery({
    queryKey: [QUERY_KEYS.QUESTION, language],
    url: language ? `${GET_QUE}?language=${encodeURIComponent(language)}` : GET_QUE,
    method: "GET",
    enabled: !!language,
  });

  const filteredQuestions = questionsResponse?.results?.questions || questionsResponse?.results || [];

  if (isQuestionsLoading) {
    return <Loader />;
  }

  const handleSave = () => {
  setFormErrors({ questionNo: "", question: "", options: [], general: "" });

  const data = formData;

  if (!data.question || !data.question.trim()) {
    setFormErrors((prev) => ({ ...prev, question: strings.questionRequired }));
    return;
  }

  const options = Array.isArray(data.options) ? data.options : [];
  const trimmedOptions = options.map((opt) => (typeof opt === "string" ? opt.trim() : ""));


  const optionErrors = Array(trimmedOptions.length).fill("");
  trimmedOptions.forEach((opt, idx) => {
    if (!opt) optionErrors[idx] = `Option ${idx + 1} is required`;
  });

  if (trimmedOptions.length !== 4 || optionErrors.some((err) => err !== "")) {
    setFormErrors({
      options: optionErrors,
    });

    toast.error("All 4 options are required"); 
    return;
  }
    if (!isEditing && (!questionNo || questionNo === "" || isNaN(Number(questionNo)))) {
      setFormErrors((prev) => ({ ...prev, questionNo: strings}));
      return;
    }
    const languageName = language || "";

    const payload = {
      question: data.question.trim(),
      options: trimmedOptions,
      notes: notes.trim() || "",
      language: languageName,
    };

    if (!isEditing) {
      payload.questionNo = Number(questionNo);
    }

    const url = isEditing ? `${UPDATE_QUE}?id=${editingId}` : ADD_QUE;

    mutate(
      {
        url,
        method: isEditing ? "PUT" : "POST",
        data: payload,
      },
      {
        onSuccess: (res) => {
          toast.success(res?.message);
          setShowActivityModal(false);
          setFormErrors({ questionNo: "", question: "", options: [], general: "" });
          resetForm();
          refetchQuestions();
          setNotes("");
        },
        onError: (error) => {
          const errorMessage = error?.response?.data?.message || error?.message;

          setShowActivityModal(false);
          toast.error(errorMessage);
        },
      }
    );
  };

const handleEdit = (q) => {
  const notesValue = q.notes ?? "";

  if (q.language && q.language !== language) {
    setLanguage(q.language);
    setActiveTab(q.language);
  }

  const form = {
    question: q.question || "",
    options: q.options && q.options.length > 0 ? q.options : [""],
  };

  setFormData(form);
  setQuestionNo(q.question_no || "");
  setEditingId(q.id);
  setIsEditing(true);

  setOriginalData({
    question: q.question || "",
    options: [...(q.options || [])],
    notes: notesValue,
    questionNo: q.question_no,
    language: q.language,
  });

  setNotes(notesValue);

  setFormErrors({ questionNo: "", question: "", options: [], general: "" });

  setShowActivityModal(true);
};

const resetForm = (langName = language) => {
  setQuestionNo("");
  setFormData({ question: "", options: [""] });
  setFormErrors({ questionNo: "", question: "", options: [], general: "" });
  setNotes(""); 
  setIsEditing(false);
  setEditingId(null);
  setLanguage(langName);
  setActiveTab(langName);
};


  const handleDelete = (qId) => {
    const found = filteredQuestions.find((q) => q.id === qId);
    if (found) {
      setSelectedQuestion(found);
      setShowDeleteModal(true);
    }
  };

  const handleConfirmDelete = () => {
    if (!selectedQuestion?.id) return;

    deleteQuestion(
      {
        url: `${DELETE_QUE}?id=${selectedQuestion.id}`,
        method: "DELETE",
      },
      {
        onSuccess: (res) => {
          toast.success(res?.message);
          setShowDeleteModal(false);
          setSelectedQuestion(null);
          refetchQuestions();
        },
        onError: (error) => {
          const errorMessage = error?.response?.data?.message || error?.message;
          setFormErrors((prev) => ({ ...prev, general: errorMessage }));
        },
      }
    );
  };
const getNextQuestionNo = () => {
  if (!filteredQuestions.length) return 1;

  return Math.max(...filteredQuestions.map((q) => Number(q.question_no))) + 1;
};

const renderAddButton = () => {
  const isLimitReached = filteredQuestions.length >= MAX_QUESTIONS;

  return (
    <div className="relative group inline-block">
      <Button
        variant="primary"
        size="md"
        disabled={isLimitReached}
        className={isLimitReached ? "opacity-60 cursor-not-allowed" : ""}
        onClick={() => {
          if (isLimitReached) return;

          resetForm(language);
          setQuestionNo(getNextQuestionNo());
          setShowActivityModal(true);
        }}
      >
        {strings.addQuestion}
      </Button>

      {isLimitReached && (
        <div
          className="
            absolute top-full left-1/2 -translate-x-1/2 mt-2
            hidden group-hover:block
            bg-gray-900 text-white text-xs px-3 py-1.5 rounded-md
            whitespace-nowrap shadow-lg z-50
          "
        >
          {strings.max8queRequired}
        </div>
      )}
    </div>
  );
};


  const renderPageHeader = (showActionButton = false) => (
    <>
      <PageHeader
        title={strings.question}
        description={strings.questiondescription}
        className="mb-6"
      >
        {showActionButton && renderAddButton()}
      </PageHeader>

      {availableLanguages.length > 0 && (
        <div className="mb-6" padding={true}>
          <LanguageTabs
            languages={availableLanguages}
            activeLanguage={language}
            onLanguageChange={handleLanguageChange}
            variant="buttons"
            useApi={false}
          />
        </div>
      )}
    </>
  );
const hasFormChanged = () => {
  if (!isEditing || !originalData) return true;

  const normalize = (val) => (Array.isArray(val) ? val.map((v) => String(v).trim()) : String(val ?? "").trim());

  return (
    normalize(originalData.question) !== normalize(formData.question) ||
    JSON.stringify(normalize(originalData.options)) !== JSON.stringify(normalize(formData.options)) ||
    normalize(originalData.notes) !== normalize(notes)
  );
};

  return (
    <div>
      <div className="mb-6">{renderPageHeader(true)}</div>

      {filteredQuestions.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <p className="text-lg font-medium text-secondary mb-2">
              {strings.noQuestionsFoundfor} {activeTab || "selected"} {strings.language}
            </p>

            {/* <div className="mt-4 flex justify-center">{renderAddButton()}</div> */}
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredQuestions.map((q) => (
            <Card key={q.id} className="group hover:shadow-lg transition-all duration-200">
              <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
                <div
                  onClick={() => {
                    if (q.notes) {
                      setSelectedNotes(q.notes);
                      setShowNotesModal(true);
                    }
                  }}
                  className={`${q.notes ? "cursor-pointer hover:opacity-80 transition-opacity" : ""}`}
                >
                  <span className="inline-flex items-center px-3 py-2.5 rounded-md text-sm font-semibold bg-primary text-white">
                    {questionNoLabel} {q.question_no}
                  </span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(q)}
                    className="p-2 hover:bg-gray-100 bg-grey rounded-md transition-colors"
                    title={strings.editQuestion}
                    aria-label={strings.editQuestion}
                  >
                    <img src={editIcon} alt={strings.edit} className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(q.id)}
                    className="p-2 hover:bg-red-50 bg-grey rounded-md transition-colors"
                    title={strings.deleteQuestion}
                    aria-label={strings.deleteQuestion}
                  >
                    <img src={deleteIcon} alt="Delete" className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="h-12 my-4 flex items-center">
                <h3
                  dir={isRtlLanguage ? "rtl" : "ltr"}
                  className={`text-base font-semibold text-white bg-primary px-4 py-1.5 rounded-lg
    w-full leading-relaxed break-words overflow-hidden line-clamp-2
    ${isRtlLanguage ? "text-right" : "text-left"}`}
                >
                  {q.question}
                </h3>
              </div>

              <div>
                <p className="text-xs font-semibold text-secondary uppercase tracking-wide mb-3">Options</p>
                <div className="space-y-2">
                  {q.options?.map((opt, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white border border-gray-200 hover:border-primary/30 hover:bg-gray-50 transition-all"
                    >
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white text-xs font-semibold flex items-center justify-center">
                        {idx + 1}
                      </span>
                      <span
                        dir={isRtlLanguage ? "rtl" : "ltr"}
                        className={`flex-1 text-sm text-secondary break-all ${
                          isRtlLanguage ? "text-right" : "text-left"
                        }`}
                      >
                        {opt}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {showActivityModal && (
        <QuestionForm
          onClose={() => setShowActivityModal(false)}
          currentLanguage={language}
          isRtl={isRtlLanguage}
          data={formData}
          setData={(updater) => {
            setFormErrors({ questionNo: "", question: "", options: [], general: "" });
            setFormData(updater);
          }}
          handleSave={handleSave}
          isEditing={isEditing}
          questionNo={questionNo}
          setQuestionNo={(val) => {
            setFormErrors((prev) => ({ ...prev, questionNo: "" }));
            setQuestionNo(val);
          }}
          notes={notes}
          setNotes={setNotes}
          errors={formErrors}
          hasFormChanged={hasFormChanged}
          isLoading={isSaving}
        />
      )}

      {showNotesModal && (
        <Modal isOpen={showNotesModal} onClose={() => setShowNotesModal(false)} title="Warning" size="md">
          <div
            dir={isRtlLanguage ? "rtl" : "ltr"}
            className={`text-sm text-secondary whitespace-pre-line leading-relaxed ${
              isRtlLanguage ? "text-right" : "text-left"
            }`}
          >
            {selectedNotes}
          </div>
        </Modal>
      )}

      {showDeleteModal && (
        <DeletModel 
          handleConfirmDelete={handleConfirmDelete} 
          handleCancelDelete={() => setShowDeleteModal(false)}
          isLoading={isDeleting}
        />
      )}
    </div>
  );
};

export default Question;
